import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Package, CreditCard, ShoppingBag, Save, Trash2, Ship, ArrowRight } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { showErrorToast, showSuccessToast } from '@/lib/toast-utils';
import { CartSelection } from './CartSelection';
import { UnitSelector } from '@/components/checkout/UnitSelector';

interface CartFlyoutProps {
  children: React.ReactNode;
}

export function CartFlyout({ children }: CartFlyoutProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [cartName, setCartName] = useState('');
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});
  const [debounceTimers, setDebounceTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({});
  const { cart, totalItems, isLoading, savedCarts, createNewCart, updateCartName, updateCartItemQuantity, removeFromCart, updateCartVin } = useCart();

  const cartVin = (cart?.orderFields?.VIN?.value && typeof cart.orderFields.VIN.value === 'string')
    ? cart.orderFields.VIN.value.trim()
    : '';
  const hasVin = Boolean(cartVin);

  const handleVinSelected = async (unit: any) => {
    const success = await updateCartVin(unit.vin);
    if (success) {
      showSuccessToast(`${unit.modelYear || ''} ${unit.make || ''} ${unit.model || ''} - HIN: ${unit.vin}`.trim(), 'HIN Added to Cart');
    }
  };

  const handleClearVin = async () => {
    await updateCartVin(null);
  };
  

  // Initialize and sync quantity inputs when cart changes
  useEffect(() => {
    if (cart?.items) {
      setQuantityInputs(prev => {
        const newQuantities: Record<string, string> = {};
        cart.items.forEach(item => {
          // Only update if we're not currently editing this item
          if (!debounceTimers[item.id]) {
            newQuantities[item.id] = item.quantity.toString();
          } else {
            // Keep the current input value if we're debouncing
            newQuantities[item.id] = prev[item.id] || item.quantity.toString();
          }
        });
        return newQuantities;
      });
    }
  }, [cart?.items]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    await updateCartItemQuantity(itemId, newQuantity);
    setUpdatingItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const handleQuantityInputChange = (itemId: string, value: string) => {
    // Update the input display immediately
    setQuantityInputs(prev => ({ ...prev, [itemId]: value }));

    // Clear existing timer for this item
    if (debounceTimers[itemId]) {
      clearTimeout(debounceTimers[itemId]);
    }

    // Validate and set new timer
    const quantity = parseInt(value);
    if (!isNaN(quantity) && quantity > 0) {
      const timer = setTimeout(() => {
        handleQuantityChange(itemId, quantity);
      }, 750);
      
      setDebounceTimers(prev => ({ ...prev, [itemId]: timer }));
    }
  };

  const handleQuantityInputBlur = (itemId: string, value: string) => {
    // Clear debounce timer
    if (debounceTimers[itemId]) {
      clearTimeout(debounceTimers[itemId]);
    }

    const quantity = parseInt(value);
    if (!isNaN(quantity) && quantity > 0) {
      handleQuantityChange(itemId, quantity);
    } else {
      // Reset to current quantity if invalid
      const item = cart?.items.find(i => i.id === itemId);
      if (item) {
        setQuantityInputs(prev => ({ ...prev, [itemId]: item.quantity.toString() }));
      }
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    const success = await removeFromCart(itemId);
    // Only remove from loading set if it failed (success means item is gone)
    if (!success) {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleSaveCart = async () => {
    if (!cartName.trim()) {
      showErrorToast('Please enter a name for your cart.', 'Cart Name Required');
      return;
    }

    try {
      if (cart?.secretId) {
        // Update existing cart name
        const success = await updateCartName(cart.secretId, cartName.trim());
        if (success) {
          setShowSaveDialog(false);
          setCartName('');
        }
      } else {
        // Create new cart - this functionality may need to be extended
        await createNewCart();
        setShowSaveDialog(false);
        setCartName('');
      }
    } catch (error) {
      showErrorToast('Failed to save cart. Please try again.');
    }
  };

  const renderCartContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center space-y-2">
            <Package className="h-8 w-8 text-muted-foreground mx-auto animate-pulse" />
            <p className="text-muted-foreground">Loading cart...</p>
          </div>
        </div>
      );
    }

    // Show cart selection if no active cart but there are saved carts
    if (!cart && savedCarts.length > 0) {
      return <CartSelection onClose={() => setIsOpen(false)} />;
    }

    // Empty cart state
    if (!cart || cart.items.filter(item => item.orderLineType === 'product').length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <ShoppingBag className="h-12 w-12 text-muted-foreground" />
          <div className="text-center space-y-2">
            <h3 className="font-medium text-foreground">Your cart is empty</h3>
            <p className="text-sm text-muted-foreground">
              Add some items to get started
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full space-y-3">
        {/* Order Type Badge */}
        <div className="flex-shrink-0">
          <Badge variant={hasVin ? 'default' : 'secondary'} className="w-full justify-center py-1">
            {hasVin ? 'VIN-Specific Order' : 'Stock Order'}
          </Badge>
        </div>

        {/* VIN Selector */}
        <div className="flex-shrink-0">
          {hasVin ? (
            <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-border bg-card">
              <div className="flex items-center gap-2 min-w-0">
                <Ship className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">VIN-Specific Order</p>
                  <p className="text-xs text-muted-foreground truncate">HIN: {cartVin}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <UnitSelector
                  trigger={
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                      Change
                    </Button>
                  }
                  onUnitSelected={handleVinSelected}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                  onClick={handleClearVin}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <UnitSelector
              trigger={
                <Button variant="outline" size="sm" className="w-full">
                  <Ship className="h-4 w-4 mr-2" />
                  Add VIN to Cart
                </Button>
              }
              onUnitSelected={handleVinSelected}
            />
          )}
        </div>

        {/* Cart Items */}
        <ScrollArea className="flex-1 pr-2 border-t border-border" type="always">
          <div className="space-y-2 pr-2">
            {cart.items.filter(item => item.orderLineType === 'product').map((item, index) => {
              // Find associated product discount for this item
              const productDiscount = cart.items.find(
                discount => discount.orderLineType === 'productDiscount' && 
                discount.productId === item.productId
              );
              
              return (
              <div 
                key={item.id} 
                className={`flex gap-3 p-3 border-b border-border transition-opacity bg-card ${updatingItems.has(item.id) ? 'opacity-50' : 'opacity-100'}`}
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.productName}
                    className="w-[55px] h-[55px] object-cover flex-shrink-0 self-center rounded"
                  />
                ) : (
                  <div className="w-[55px] h-[55px] bg-muted/50 border border-border flex items-center justify-center flex-shrink-0 self-center rounded">
                    <Package className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {/* Name and total */}
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {item.productName}
                    </h4>
                    <span className="text-sm font-semibold text-foreground flex-shrink-0">
                      {formatCurrency(item.totalPrice, cart.currencyCode)}
                    </span>
                  </div>
                  {/* Part number, unit price, and quantity+delete inline */}
                  <div className="flex items-center justify-between gap-2 mt-[5px]">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        #{item.productId} • {formatCurrency(item.unitPrice, cart.currencyCode)} ea
                      </p>
                      {productDiscount && (
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium mt-0.5">
                          {productDiscount.productName}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={quantityInputs[item.id] || item.quantity}
                        onChange={(e) => handleQuantityInputChange(item.id, e.target.value)}
                        onBlur={(e) => handleQuantityInputBlur(item.id, e.target.value)}
                        disabled={updatingItems.has(item.id)}
                        className="w-12 h-7 text-center text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={updatingItems.has(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </ScrollArea>

        {/* Cart Summary */}
        <div className="border-t border-border pt-3 space-y-2 flex-shrink-0">

          <div className="space-y-1.5">
            {cart.subtotalAmount !== cart.totalAmount && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Subtotal:</span>
                <span className="text-sm text-foreground">
                  {formatCurrency(cart.subtotalAmount, cart.currencyCode)}
                </span>
              </div>
            )}
            {/* Order Discounts */}
            {cart.items.filter(item => item.orderLineType === 'discount').map(discount => (
              <div key={discount.id} className="flex justify-between items-center text-green-600 dark:text-green-400">
                <span className="text-xs">{discount.productName}:</span>
                <span className="text-xs font-medium">
                  {formatCurrency(discount.totalPrice, cart.currencyCode)}
                </span>
              </div>
            ))}
            {/* Taxes */}
            {cart.items.filter(item => item.orderLineType?.toLowerCase() === 'tax').map(tax => (
              <div key={tax.id} className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">{tax.productName}:</span>
                <span className="text-xs text-foreground">
                  {formatCurrency(tax.totalPrice, cart.currencyCode)}
                </span>
              </div>
            ))}
            {cart.shippingFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Shipping ({cart.shippingMethod?.name || 'Standard'}):
                </span>
                <span className="text-sm text-foreground">
                  {formatCurrency(cart.shippingFee, cart.currencyCode)}
                </span>
              </div>
            )}
            {cart.paymentFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Payment Fee ({cart.paymentMethod?.name || 'Standard'}):
                </span>
                <span className="text-sm text-foreground">
                  {formatCurrency(cart.paymentFee, cart.currencyCode)}
                </span>
              </div>
            )}
            <div className="border-t border-border pt-2">
              <div className="flex justify-between items-center">
                <span className="font-medium text-foreground">Total:</span>
                <span className="font-semibold text-lg text-foreground">
                  {formatCurrency(cart.totalAmount, cart.currencyCode)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-1.5">
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => {
                setIsOpen(false);
                window.location.href = '/checkout';
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Go to Checkout
            </Button>
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="secondary" 
                  className="w-full"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Cart
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Save Cart</DialogTitle>
                  <DialogDescription>
                    Give your cart a name to save it for later.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="cart-name">Cart Name</Label>
                    <Input
                      id="cart-name"
                      placeholder="Enter cart name..."
                      value={cartName}
                      onChange={(e) => setCartName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveCart();
                        }
                      }}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowSaveDialog(false);
                      setCartName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveCart}>
                    Save Cart
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="space-y-2 flex-shrink-0">
          <div className="flex items-center justify-between gap-2 pr-8">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Shopping Cart
              {totalItems > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {totalItems}
                </Badge>
              )}
            </SheetTitle>
            {cart && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/cart';
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus:outline-none"
              >
                View full cart
                <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
          <SheetDescription>
            {cart ? 
              `Review your items and proceed to checkout` : 
              'Your shopping cart is ready for items'
            }
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 flex-1 overflow-hidden flex flex-col">
          {renderCartContent()}
        </div>
      </SheetContent>
    </Sheet>
  );
}